import React from 'react';
import SelectField from '../../../../common/Input/SelectField';
import RichTextEditor from '../../../../common/RichTextEditor';
import { FaToggleOn, FaToggleOff } from 'react-icons/fa';

const FormInput = ({
  field,
  label,
  value,
  onChange,
  inputType = 'text',
  lookupOptions = [],
  isLoadingLookups = false,
  placeholder = '',
  disabled = false,
  readOnly = false,
  error = null,
  themeColor = 'primary',
  required = false,
  showForeignKeyInfo = false, // AGGIUNTA: Nuova prop per controllare le info aggiuntive
}) => {

  const inputId = `form-input-${field}`;

  const commonInputClasses = `w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border rounded-md shadow-sm focus:outline-none focus:ring-2 dark:border-gray-600
    ${error ? `border-red-500 dark:border-red-400 focus:ring-red-500/50 focus:border-red-500` : `border-gray-300 dark:border-gray-500 focus:ring-${themeColor}-500/50 focus:border-${themeColor}-500`}
    ${(disabled || readOnly) ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-70' : ''}`;

  const handleChange = (e) => {
    const { value: targetValue, type, checked } = e.target;
    onChange(field, type === 'checkbox' ? checked : targetValue);
  };

  const handleSelectChange = (selectedValue) => {
    // Se il valore è 0 (clear da SelectField) e le opzioni hanno valori stringa,
    // usa stringa vuota invece di 0 (per campi VARCHAR come ATT_ValidazioneZod)
    if (selectedValue === 0 && lookupOptions?.length > 0) {
      const hasStringValues = lookupOptions.some(opt => typeof opt.value === 'string');
      if (hasStringValues) {
        onChange(field, '');
        return;
      }
    }
    onChange(field, selectedValue);
  };

  const handleBooleanToggle = () => {
    onChange(field, !value);
  };

  if (field === 'CAM_ListaInFiltro') { // SOLO CAM_ListaInFiltro ora, ATT_Lista rimosso
    const listaInFiltroOptions = [
      { value: 1, label: 'Lista' },
      { value: 2, label: 'Singolo' },
      { value: 3, label: 'Entrambe' },
    ];
    return (
      <div className="mb-3 w-full">
        <label
          htmlFor={inputId}
          className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <SelectField
          id={inputId}
          name={field}
          options={listaInFiltroOptions}
          value={value}
          onChange={handleSelectChange}
          disabled={disabled || isLoadingLookups}
          isLoading={isLoadingLookups}
          isClearable={!required}
          placeholder={placeholder || `-- Seleziona ${label} --`}
          required={required}
          error={error}
          showForeignKeyInfo={showForeignKeyInfo}
        />
        {error && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>}
      </div>
    );
  }

  if (inputType === 'select') {
    return (
      <SelectField
        id={inputId}
        name={field}
        label={label}
        options={lookupOptions || []}
        value={value}
        onChange={handleSelectChange}
        disabled={disabled || isLoadingLookups}
        isLoading={isLoadingLookups}
        isClearable={!required}
        placeholder={placeholder || `-- Seleziona ${label} --`}
        required={required}
        error={error}
        className="mb-3 w-full"
        showForeignKeyInfo={showForeignKeyInfo} // AGGIUNTA: Passa la prop
      />
    );
  }

  const renderInput = () => {
    switch (inputType) {
      case 'richtext':
        return (
          <RichTextEditor
            id={field}
            value={value || ''}
            onChange={(content) => onChange(field, content)}
            disabled={disabled || readOnly}
            readOnly={readOnly}
            placeholder={placeholder || `Inserisci ${label}...`}
            error={error}
            themeColor={themeColor}
            minHeight="120px"
            maxHeight="350px"
          />
        );
      case 'textarea':
        return (
          <div className="relative">
            <textarea
              id={field}
              name={field}
              value={value || ''}
              onChange={handleChange}
              disabled={readOnly}
              rows={3}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm placeholder-gray-500 dark:placeholder-gray-400 resize"
              style={{ minHeight: '72px' }}
              onDoubleClick={(e) => {
                const textarea = e.target;
                if (!textarea.dataset.originalHeight) {
                  textarea.dataset.originalHeight = '72';
                }
                const originalHeight = parseInt(textarea.dataset.originalHeight, 10);
                const currentHeight = textarea.offsetHeight;
                const clickX = e.nativeEvent.offsetX;
                const clickY = e.nativeEvent.offsetY;
                const isInCorner = clickX > (textarea.offsetWidth - 20) && clickY > (currentHeight - 20);
                textarea.style.height = 'auto';
                const scrollHeight = textarea.scrollHeight;
                const isExpanded = currentHeight > originalHeight + 20;
                if (isExpanded && isInCorner) {
                  textarea.style.height = `${originalHeight}px`;
                  textarea.dataset.expanded = 'false';
                } else if (!isExpanded) {
                  const newHeight = Math.max(originalHeight, scrollHeight + 4);
                  textarea.style.height = `${newHeight}px`;
                  textarea.dataset.expanded = 'true';
                } else {
                  textarea.style.height = `${currentHeight}px`;
                }
              }}
            />
            <span
              className="absolute bottom-1 right-2 text-[10px] text-gray-400 dark:text-gray-500 pointer-events-none select-none"
              title="Doppio click per espandere, doppio click nell'angolo in basso a destra per comprimere"
            >
              ↕ Doppio click = espandi
            </span>
          </div>
        );
      case 'boolean':
        return (
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleBooleanToggle}
              disabled={readOnly}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2
                ${!!value 
                  ? `bg-${themeColor}-600 focus:ring-${themeColor}-500` 
                  : 'bg-gray-200 dark:bg-gray-600 focus:ring-gray-300 dark:focus:ring-gray-500'}
                ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${!!value ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {!!value ? 'Sì' : 'No'}
            </span>
          </div>
        );
      // AGGIUNTO: Supporto per checkbox (simile a boolean ma con stile diverso)
      case 'checkbox':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id={field}
              name={field}
              checked={!!value}
              onChange={handleChange}
              disabled={readOnly}
              className={`
                h-4 w-4 rounded border-gray-300 dark:border-gray-600 
                text-${themeColor}-600 focus:ring-${themeColor}-500 
                dark:bg-gray-700 dark:focus:ring-${themeColor}-400
                ${readOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            />
            <label 
              htmlFor={field} 
              className={`text-sm text-gray-700 dark:text-gray-300 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {!!value ? 'Attivo' : 'Inattivo'}
            </label>
          </div>
        );
      case 'date':
      case 'datetime-local':
        return (
          <input
            type={inputType}
            id={field}
            name={field}
            value={value || ''}
            onChange={handleChange}
            disabled={readOnly}
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            id={inputId}
            name={field}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder || label}
            disabled={disabled}
            readOnly={readOnly}
            className={commonInputClasses}
          />
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            id={inputId}
            name={field}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder || label}
            disabled={disabled}
            readOnly={readOnly}
            className={commonInputClasses}
          />
        );
    }
  };

  return (
    <div className={`w-full ${inputType === 'boolean' ? 'mb-2' : 'mb-3'}`}> {/* RIDOTTO margin per booleani */}
      <label
        htmlFor={inputId}
        className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {renderInput()}
      {error && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default FormInput;